import {
  BYOItem,
  BYOStepId,
  BuildYourOwnModule as BuildYourOwnModuleType,
  LinkVariant,
} from 'api/contentful/contentTypes.cloverdotcom';
import cx from 'classnames';
import { BusyLoader } from 'components/BusyLoader/BusyLoader';
import { SomethingWentWrong } from 'components/ErrorPage/SomethingWentWrong';
import { Grid } from 'components/Grid/Grid';
import { Overlay } from 'components/Overlay/Overlay';
import { StructuredModal } from 'components/StructuredModal/StructuredModal';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppType } from 'services/cms/typesUtil';
import useDeepCompareEffect from 'use-deep-compare-effect';
import styles from './BuildYourOwnModule.module.scss';
import { BYOItemTile, BottomNavBYO } from './components';
import { useBuildYourOwn } from './hooks/useBuildYourOwn';
import { Button } from 'components/Button/Button';
import { TextV2 } from 'components/TextV2/TextV2';
import { AccessoriesNotFound } from './components/AccessoriesNotFound/AccessoriesNotFound';
import { BUILD_YOUR_OWN_MODULE_TEST_ID } from './BuildYourOwnModule.const';
import { changePhoneNumberDynamicallyAfterTimeout } from 'utils/InvocaUtils';
import { scrollToTop } from 'utils/ScrollUtils';
import { BackButton } from './components/BackButton/BackButton';

export const BYOStepOrder = [
  BYOStepId.BUSINESS_OFFERING,
  BYOStepId.POS_NEEDS,
  BYOStepId.DEVICES,
  BYOStepId.ACCESSORIES,
];

export type BuildYourOwnModuleProps = AppType.Component<BuildYourOwnModuleType>;

export const BuildYourOwnModule: FC<BuildYourOwnModuleProps> = ({ _attributes, ...rest }) => {
  const { t } = useTranslation();
  const {
    tryPopulateBasedOnURL,
    currentStepData,
    setByoData,
    filteredItemsForStep,
    currentStepId,
    showMaxQuantityModal,
    setShowMaxQuantityModal,
    disclaimer,
    hasDCSError,
    isDCSLoading,
    handleNextClick,
    addAllCompatibleAccessories,
    removeAllCompatibleAccessories,
    moveToStep,
    selectionState,
    hydratedSubHeadlineWithSoftwarePlanAdditionalDeviceFee,
    addItemIcon,
    selectedItemCustomIcon,
  } = useBuildYourOwn();

  const [showDrawer, setShowDrawer] = useState(false);
  const { headline, subHeadline, bypassText, isImageClickable } = currentStepData ?? {};
  const isBusinessOfferingStep = currentStepId === BYOStepId.BUSINESS_OFFERING;
  const isAccessoriesStep = currentStepId === BYOStepId.ACCESSORIES;
  const items = filteredItemsForStep(currentStepId);

  useEffect(() => {
    if (currentStepId === BYOStepId.ACCESSORIES) {
      const accessories = filteredItemsForStep(BYOStepId.ACCESSORIES);
      removeAllCompatibleAccessories(); // remove old compatible accessories from selection state
      addAllCompatibleAccessories(accessories);
    }
  }, [currentStepId]);

  const shouldShowAccessoriesNotFound = isAccessoriesStep && !items.length;

  useDeepCompareEffect(() => {
    setByoData(rest);
    tryPopulateBasedOnURL();
  }, [rest, setByoData, tryPopulateBasedOnURL]);

  useEffect(() => {
    scrollToTop();
  }, [currentStepId]);

  useEffect(() => {
    if (subHeadline) {
      changePhoneNumberDynamicallyAfterTimeout();
    }
  }, [subHeadline]);

  const backButtonClick = useCallback(() => {
    moveToStep(BYOStepOrder[BYOStepOrder.indexOf(currentStepId as BYOStepId) - 1]);
  }, [currentStepId]);

  if (!currentStepData) return null;

  if (hasDCSError)
    return (
      <div className={styles.errorPageWrapper}>
        <SomethingWentWrong />
      </div>
    );

  if (isDCSLoading)
    return (
      <div className={styles.errorLoadingWrapper}>
        <BusyLoader />
      </div>
    );
  return (
    <div {..._attributes} className={cx(_attributes?.className, styles.byoContainer)}>
      <Overlay isActive={showDrawer} />
      <BackButton isVisible={!isBusinessOfferingStep} onBackButtonClick={backButtonClick} />
      <div aria-hidden={showDrawer} className={styles.byoContent}>
        <div className={styles.headlineContainer}>
          <h2 className={styles.headline}>{headline}</h2>
          {!bypassText && hydratedSubHeadlineWithSoftwarePlanAdditionalDeviceFee && (
            <TextV2
              className={styles.subHeadline}
              variant="h5"
              value={hydratedSubHeadlineWithSoftwarePlanAdditionalDeviceFee}
              data-testid={BUILD_YOUR_OWN_MODULE_TEST_ID.subHeadline}
              isMarkdown
            />
          )}
          {bypassText && (
            <Button
              variant={LinkVariant.TEXT}
              onClick={event => handleNextClick(event, selectionState)}
              className={cx(styles.subHeadline, styles.bypassText)}
            >
              {bypassText}
            </Button>
          )}
        </div>
        <Grid
          container
          gutter
          className={cx(styles.byoItemContainer, {
            [styles.byoItemContainerBusinessStepAlignment]: isBusinessOfferingStep,
          })}
        >
          {items.map((item: BYOItem) => (
            <Grid item xs={12} md={6} lg={12} key={item.itemKey}>
              <BYOItemTile
                {...item}
                isImageClickable={isImageClickable}
                addItemIcon={addItemIcon}
                selectedItemCustomIcon={selectedItemCustomIcon}
              />
            </Grid>
          ))}
        </Grid>
        {shouldShowAccessoriesNotFound && <AccessoriesNotFound />}
      </div>
      <BottomNavBYO showDrawer={showDrawer} setShowDrawer={setShowDrawer} />
      <StructuredModal
        shouldCloseOnEsc
        modalState={[showMaxQuantityModal, () => setShowMaxQuantityModal(!showMaxQuantityModal)]}
        title={t('build-your-own.modal.title')}
        description={disclaimer}
        actions={[
          {
            label: t('build-your-own.modal.confirm'),
            variant: LinkVariant.PRIMARY,
            action: () => setShowMaxQuantityModal(!showMaxQuantityModal),
          },
        ]}
      />
    </div>
  );
};
